/**
 * Outcome ladder for experiment results: click → trial → payment → revenue.
 *
 * Each step is counted on its own. The API's click goal counts every click on
 * the page (nav, logo, tabs), so trial and demo intent are rebuilt here from
 * the per-target click breakdown. Stripe steps keep landing after a visitor
 * enters the test (a 14-day trial pays out up to 14 days later), so those rows
 * say when they settle instead of pretending to be final.
 */

export type CtaKind = "trial" | "demo"

const DEMO_PATTERN =
  /(book[\s_-]*(a[\s_-]*)?(call|demo|meeting)|request[\s_-]*(a[\s_-]*)?demo|schedule[\s_-]*(a[\s_-]*)?(call|demo)|talk[\s_-]*to[\s_-]*sales|contact[\s_-]*sales|get[\s_-]*a[\s_-]*demo)/i

const TRIAL_PATTERN =
  /(start[\s_-]*(your[\s_-]*)?(free[\s_-]*)?trial|free[\s_-]*trial|try[\s_-]*(it[\s_-]*)?(for[\s_-]*)?free|start[\s_-]*(for[\s_-]*)?free|get[\s_-]*started|sign[\s_-]*up|signup|create[\s_-]*(an?[\s_-]*)?account)/i

/**
 * Classify a clicked element as trial or demo intent from its tracked target
 * (element id / label) and secondary (href or text). Demo wins over trial so
 * "Book a demo" never counts as a trial click.
 */
export function classifyCta(target: string | null | undefined, secondary?: string | null): CtaKind | null {
  const haystack = `${target ?? ""} ${secondary ?? ""}`
  if (!haystack.trim()) return null
  if (DEMO_PATTERN.test(haystack)) return "demo"
  if (TRIAL_PATTERN.test(haystack)) return "trial"
  return null
}

export type ClickEventLike = {
  target: string
  secondary: string | null
  clicks: Record<string, number>
}

/** Sum clicks per variant letter for every button of one kind. */
export function sumCtaClicks(events: ClickEventLike[], kind: CtaKind): Record<string, number> {
  const totals: Record<string, number> = {}
  for (const event of events) {
    if (classifyCta(event.target, event.secondary) !== kind) continue
    for (const [letter, count] of Object.entries(event.clicks)) {
      totals[letter] = (totals[letter] ?? 0) + count
    }
  }
  return totals
}

/**
 * How many distinct buttons of this kind were found, for the row hint. Keyed
 * by target: the same button reports a different href per variant.
 */
export function countCtaTargets(events: ClickEventLike[], kind: CtaKind): number {
  const targets = new Set(
    events.filter((e) => classifyCta(e.target, e.secondary) === kind).map((e) => e.target || e.secondary || ""),
  )
  return targets.size
}

export const TRIAL_DAYS = 14
const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Stripe outcomes (trials converting to paid, revenue) keep arriving until
 * TRIAL_DAYS after the last visitor entered. For a running test that is
 * "today + 14 days"; for a finished test it is "ended + 14 days".
 */
export function outcomeSettling({
  isActive,
  endedAt,
  now = new Date(),
  trialDays = TRIAL_DAYS,
}: {
  isActive: boolean
  endedAt?: string | Date | null
  now?: Date
  trialDays?: number
}): { settling: boolean; settlesOn: Date } {
  const lastEntry = isActive || !endedAt ? now : new Date(endedAt)
  const base = Number.isNaN(lastEntry.getTime()) ? now : lastEntry
  const settlesOn = new Date(base.getTime() + trialDays * DAY_MS)
  return { settling: now.getTime() < settlesOn.getTime(), settlesOn }
}

const APP_HOST = /^(app|dashboard|portal|my|account|console|admin)\./i
const APP_PATH = /^\/(app|dashboard|login|log-in|signin|sign-in|account)(\/|$|\?)/i

/**
 * True when a destination-page goal would also count returning customers:
 * an app subdomain or a login/dashboard path is visited by everyone who logs
 * in, not only by people who just signed up.
 */
export function destinationCountsLogins(url: string | null | undefined): boolean {
  const raw = (url ?? "").trim()
  if (!raw) return false
  try {
    const hasHost = /^https?:\/\//i.test(raw) || (!raw.startsWith("/") && raw.includes("."))
    const parsed = new URL(hasHost ? (/^https?:\/\//i.test(raw) ? raw : `https://${raw}`) : `https://site.invalid${raw.startsWith("/") ? "" : "/"}${raw}`)
    const host = parsed.hostname.replace(/^www\./i, "")
    return (hasHost && APP_HOST.test(host)) || APP_PATH.test(parsed.pathname)
  } catch {
    return false
  }
}
